import { match } from "ts-pattern";
import type { Promisable, ReadonlyDeep } from "type-fest";

import type {
  AllocateResponsibleUserRequest,
  EndInvolvementsRequest,
  PersonId,
  PersonInvolvementId,
  PersonWithIdentityWithheld,
  ReplaceResponsibleUserRequest,
  StartInvolvementsRequest,
} from "@thalamos/haven-common";
import { organisationsInvolvedWithPerson } from "@thalamos/haven-common";

import type {
  AdminPersonAnswer,
  PersonChange,
} from "../../api/adminPerson/api.js";
import type { CaseInvolvementActor } from "../../components/CaseInvolvement/CaseInvolvement.js";
import type { GuestAccessEndingOutcome } from "../../components/EndGuestAccessDialog/EndGuestAccessDialog.js";
import type { InvolvementOutcome } from "../../components/InvolvementDialogs/InvolvementDialogs.js";
import {
  actorConveyed,
  partySearch,
  type InvolvementApi,
  type InvolvementManagement,
  type ResponsibleUserAllocation,
} from "../Persons/involvementManagement.js";

/** The calls a Thalamos admin's changes make, each answering with what to show next. */
export type AdminInvolvementApi = Pick<
  InvolvementApi,
  "findUsers" | "findOrganisations"
> & {
  start: (
    change: PersonChange<StartInvolvementsRequest>,
  ) => Promisable<AdminPersonAnswer>;
  end: (
    change: PersonChange<EndInvolvementsRequest>,
  ) => Promisable<AdminPersonAnswer>;
  allocateResponsibleUser: (
    change: PersonChange<AllocateResponsibleUserRequest>,
  ) => Promisable<AdminPersonAnswer>;
  replaceResponsibleUser: (
    change: PersonChange<ReplaceResponsibleUserRequest>,
  ) => Promisable<AdminPersonAnswer>;
  readPerson: (personId: PersonId) => Promisable<AdminPersonAnswer>;
  endGuestAccess: (guest: {
    personId: PersonWithIdentityWithheld["personId"];
    involvementId: PersonInvolvementId;
  }) => Promisable<AdminPersonAnswer>;
};

/**
 * What the case involvement controls need to let a Thalamos admin change who
 * is involved, worked out from the view the server sent rather than from the
 * record, which a Thalamos admin never holds. A change shows the view it
 * answers with instead of reading the record again, which only a recorded read
 * may do.
 */
export const adminInvolvementManagement = ({
  view,
  api,
  onView,
}: {
  view: ReadonlyDeep<PersonWithIdentityWithheld>;
  api: AdminInvolvementApi;
  onView: (view: PersonWithIdentityWithheld) => Promisable<void>;
}): {
  actor: CaseInvolvementActor | undefined;
  management: InvolvementManagement | undefined;
  allocation: ResponsibleUserAllocation | undefined;
  /** Absent unless a Thalamos admin may end a guest's access here. */
  onEndGuest:
    | ((
        involvementId: PersonInvolvementId,
      ) => Promise<GuestAccessEndingOutcome>)
    | undefined;
} => {
  const { personId, involvements, actions } = view;
  // A Thalamos admin is no part of the child's care, so only the bodies in it
  // count as near when naming somebody.
  const search = partySearch({
    api,
    near: organisationsInvolvedWithPerson({ involvements: involvements }),
  });

  const landed = async ({
    write,
    expected,
  }: {
    write: Promisable<AdminPersonAnswer>;
    expected: number;
  }): Promise<InvolvementOutcome> => {
    return match(await write)
      .with({ kind: "view" }, async ({ status, view: next }) => {
        if (status !== expected) {
          return "failed" as const;
        }

        await onView(next);
        return "done" as const;
      })
      .with({ kind: "refused", status: 409 }, () => "changed" as const)
      .with(
        { kind: "refused" },
        { kind: "malformed" },
        { kind: "unanswered" },
        () => "failed" as const,
      )
      .exhaustive();
  };

  // Shows the view read afresh; one that cannot be read leaves the page as it
  // was.
  const onRefresh = async () => {
    const answer = await api.readPerson(personId);
    if (answer.kind === "view") {
      await onView(answer.view);
    }
  };

  return {
    actor: actorConveyed(actions),
    management: actions.includes("assign")
      ? {
          ...search,
          onAdd: (request) =>
            landed({ write: api.start({ personId, request }), expected: 201 }),
          onEnd: (request) =>
            landed({ write: api.end({ personId, request }), expected: 200 }),
          onReplaceResponsibleUser: (request) =>
            landed({
              write: api.replaceResponsibleUser({ personId, request }),
              expected: 200,
            }),
          onRefresh,
        }
      : undefined,
    allocation: actions.includes("allocate_responsible_user")
      ? {
          ...search,
          onAllocateResponsibleUser: (request) =>
            landed({
              write: api.allocateResponsibleUser({ personId, request }),
              expected: 201,
            }),
          onRefresh,
        }
      : undefined,
    onEndGuest: actions.includes("end_guest_access")
      ? async (involvementId) =>
          (await landed({
            write: api.endGuestAccess({ personId, involvementId }),
            expected: 200,
          })) === "done"
            ? "ended"
            : "failed"
      : undefined,
  };
};
